import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { balanceDue, effStatus, fmtDate, fmtMoney, fmtShort, returnDate, tripTotal } from "@/lib/trip";
import { Card, Countdown, StatusBadge } from "@/components/common";

export function TripCard({ trip, client, to, paidExtra = 0 }) {
  const bal = balanceDue(trip, paidExtra);
  const status = effStatus(trip);
  return (
    <Link to={to} data-testid={`trip-card-${trip.id}`} className="group block">
      <Card className="h-full overflow-hidden transition-shadow hover:shadow-[0_8px_24px_rgba(15,30,54,0.08)]">
        <div className="relative h-36 overflow-hidden bg-slate-100">
          <img src={trip.image} alt={trip.destination} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-navy-950/10 to-transparent" />
          {status !== "COMPLETED" && <Countdown date={trip.departure} className="absolute right-3 top-3" />}
          <p className="absolute bottom-3 left-4 right-4 truncate text-sm font-semibold text-white">{trip.destination}</p>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {client && <p className="truncate text-sm font-semibold text-navy-900">{client.name}</p>}
              <p className={cn("truncate", client ? "text-xs text-slate-500" : "text-sm font-semibold text-navy-900")}>{trip.title}</p>
            </div>
            <StatusBadge status={status} className="shrink-0" />
          </div>
          <div className="mt-4 flex items-end justify-between gap-3 border-t border-slate-100 pt-3">
            <div className="min-w-0 text-xs text-slate-500">
              <p className="truncate">{trip.vessel}</p>
              <p className="mt-0.5">{fmtShort(trip.departure)} – {fmtDate(returnDate(trip))}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-sm font-bold text-navy-900">{fmtMoney(tripTotal(trip))}</p>
              <p className={cn("text-[11px]", bal > 0 && status === "BOOKED" ? "text-amber-600" : "text-slate-400")}>
                {status === "QUOTE" ? "Quoted" : bal > 0 ? `${fmtMoney(bal)} due` : "Paid in full"}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function TripRow({ trip, sub, to, right }) {
  return (
    <Link to={to} data-testid={`trip-row-${trip.id}`} className="group flex items-center gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-slate-50">
      <img src={trip.image} alt="" loading="lazy" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-navy-900">{trip.title}</p>
        <p className="truncate text-xs text-slate-500">{sub || `${trip.vessel} · ${fmtDate(trip.departure)}`}</p>
      </div>
      {right ?? <StatusBadge status={trip.status} className="hidden sm:inline-flex" />}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
